/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { DateTime } from 'luxon';

import { ActionButton, BrandColors, Modal, RadioButtonInput } from '@blend/enterprise-components';

import { TaskHeaders } from 'blb-components/AuthenticatedApp/DashboardPage/Task/constants';
import { ModalContent } from 'blb-components/ModalContent';
import { remCalc } from 'blb-styles/functions';

const styles = StyleSheet.create({
  fieldset: {
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: remCalc(32),
  },
  formContent: {
    color: BrandColors.NIGHT_80,
  },
  formHeader: {
    fontSize: remCalc(14),
    lineHeight: remCalc(20),
    marginBottom: remCalc(16),
  },
  radioLabel: {
    ':first-child': {
      marginBottom: remCalc(20),
    },
  },
});

export namespace UnsignedESignModal {
  export interface Props {
    handleClose: (hasAcknowledged: boolean) => void;
    isOpen: boolean;
    taskId?: string;
  }

  export type Acknowledgement = 'pending' | 'included';
}

export const UnsignedESignModal: React.FC<UnsignedESignModal.Props> = ({ handleClose, isOpen, taskId }) => {
  const [submitButtonDisabled, setSubmitButtonDisbled] = useState<boolean>(true);
  const [acknowledgement, setAcknowledgement] = useState<UnsignedESignModal.Acknowledgement | null>(null);

  const title = TaskHeaders.UNSIGNED_ESIGN_DOCUMENTS;

  const date = DateTime.local().toFormat("h:mm a ZZZZ 'on' MMMM d");

  const pendingStatus: UnsignedESignModal.Acknowledgement = 'pending';
  const includedStatus: UnsignedESignModal.Acknowledgement = 'included';

  const disabledReason = 'Please acknowledge that the borrower has not signed eSign documents.';

  const taskIdString = taskId ? `upload-task-${taskId}` : `upload-task`;
  const taskKeyString = taskId ? `upload-task-${taskId}-key` : `upload-task-key`;

  const onRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const acknowledgement = event.target.value as UnsignedESignModal.Acknowledgement;
    setAcknowledgement(acknowledgement);
    if (submitButtonDisabled) {
      setSubmitButtonDisbled(false);
    }
  };

  const isRadioChecked = (value: UnsignedESignModal.Acknowledgement): boolean => value === acknowledgement;

  const onClose = () => {
    // passing `false` as acknowledgement as "continue" button was not clicked
    handleClose(false);
  };

  const onContinueClick = () => {
    // handle passing of acknowledgement on the click of the button
    // so that "escape" closing the modal doesn't trigger "accept" of modal
    const hasAcknowledged: boolean = acknowledgement !== null;
    handleClose(hasAcknowledged);
  };

  const radioOptions: RadioButtonInput.Props[] = [
    {
      checked: isRadioChecked(pendingStatus),
      id: `${taskIdString}-unsigned-modal-pending-contact`,
      key: `${taskKeyString}-unsigned-modal-pending-contact`,
      label: 'I will contact the borrower and ensure that they will eSign their sign ahead closing documents.',
      labelSize: 'sm',
      name: `${taskIdString}-unsigned-modal`,
      onChange: onRadioChange,
      value: pendingStatus,
    },
    {
      checked: isRadioChecked(includedStatus),
      id: `${taskIdString}-unsigned-modal-signed-docs-included`,
      key: `${taskKeyString}-unsigned-modal-signed-docs-included`,
      label:
        'The signed closing package that I am uploading contains copies of the sign ahead closing documents that were signed by the borrower during the closing appointment.',
      labelSize: 'sm',
      name: `${taskIdString}-unsigned-modal`,
      onChange: onRadioChange,
      value: includedStatus,
    },
  ];

  return (
    <Modal contentLabel={title} handleClose={onClose} isOpen={isOpen}>
      <ModalContent handleClose={undefined} headerText={title} subHeaderText={undefined}>
        <form className={css(styles.formContent)} onSubmit={onClose}>
          <p className={css(styles.formHeader)}>
            As of {date}, the borrower has not eSigned their sign ahead closing documents. In order to ensure that the
            closing completes in a timely fashion, please acknowledge one of the following options before proceeding:
          </p>
          <fieldset className={css(styles.fieldset)}>
            {radioOptions.map((propsObject: RadioButtonInput.Props) => (
              <label className={css(styles.radioLabel)} key={`label-${propsObject.key}`}>
                <RadioButtonInput {...propsObject} />
              </label>
            ))}
          </fieldset>
          <div className={css(styles.footer)}>
            <ActionButton
              disabled={submitButtonDisabled}
              id={`${taskIdString}-unsigned-modal-submit`}
              isLoading={false}
              label="Continue"
              onClick={onContinueClick}
              theme="primary"
              tooltipText={disabledReason}
            />
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};
